import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { GusPaymentMethodForm } from '../../models/payment-method.models';
import {
  StripeElements,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  StripeElementsOptions,
  StripeCardElementOptions,
  CreatePaymentMethodCardData,
  PaymentMethodResult,
  StripeElement,
  StripeElementChangeEvent,
} from '@stripe/stripe-js';
import { StripeService } from 'ngx-stripe';
import { combineLatest, EMPTY, map, Observable, of, Subscriber, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type StripeElementWithHandlers = StripeElement & {
  once(eventType: 'ready', handler: () => void): StripeElement;
  on(eventType: 'change', handler: (event: StripeElementChangeEvent) => void): StripeElement;
  off(eventType: 'change', handler?: (event: StripeElementChangeEvent) => void): StripeElement;
};

@Component({
  selector: 'gus-card-payment-method',
  templateUrl: './card-payment-method.component.html',
  styleUrl: './card-payment-method.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: GusPaymentMethodForm, useExisting: forwardRef(() => CardPaymentMethodComponent) }],
})
export class CardPaymentMethodComponent extends GusPaymentMethodForm {
  stripeElements!: StripeElements;
  cardNumberElement!: StripeCardNumberElement;
  cardExpiryElement!: StripeCardExpiryElement;
  cardCvcElement!: StripeCardCvcElement;

  readonly #destroyRef: DestroyRef = inject(DestroyRef);

  readonly #isValid: WritableSignal<boolean> = signal(false);

  override isValid: Signal<boolean> = computed(() => this.#isValid());

  readonly cardNumber: Signal<ElementRef<HTMLElement>> = viewChild.required('cardNumber');
  readonly cardExpiry: Signal<ElementRef<HTMLElement>> = viewChild.required('cardExpiry');
  readonly cardCvc: Signal<ElementRef<HTMLElement>> = viewChild.required('cardCvc');

  readonly elementsOptions: StripeElementsOptions = {
    locale: 'en',
    fonts: [{ family: 'GreycliffCF-Regular', src: `url(/fonts/GreycliffCF-Regular.ttf)` }],
  };

  initializing: WritableSignal<boolean> = signal(true);

  private initializationStarted: boolean = false;

  constructor(public stripeService: StripeService) {
    super();
  }

  override initializePaymentDetails(): void {
    if (this.initializationStarted) return;

    this.initializationStarted = true;

    this.createStripeElements();
  }

  override createMethod(): Observable<PaymentMethodResult> {
    const payload: CreatePaymentMethodCardData = { type: 'card', card: this.cardNumberElement };

    return this.stripeService
      .createPaymentMethod(payload)
      .pipe(switchMap((result: PaymentMethodResult) => (result.error ? EMPTY : of(result))));
  }

  private createStripeElements(): void {
    this.stripeService.elements(this.elementsOptions).subscribe((elements: StripeElements) => {
      this.stripeElements = elements;

      this.createCardNumberElement();
      this.createCardExpiryElement();
      this.createCardCvcElement();

      this.onElementsReady().subscribe(() => this.initializing.set(false));

      this.handleElementsValueChange();
    });
  }

  private createCardNumberElement(): void {
    this.cardNumberElement = this.stripeElements.create('cardNumber', {
      ...this.generateElementOptions(),
      placeholder: 'XXXX XXXX XXXX XXXX',
    });

    this.cardNumberElement.mount(this.cardNumber().nativeElement);
  }

  private createCardExpiryElement(): void {
    this.cardExpiryElement = this.stripeElements.create('cardExpiry', {
      ...this.generateElementOptions(true),
      placeholder: 'MM / YY',
    });

    this.cardExpiryElement.mount(this.cardExpiry().nativeElement);
  }

  private createCardCvcElement(): void {
    this.cardCvcElement = this.stripeElements.create('cardCvc', {
      ...this.generateElementOptions(true),
      placeholder: 'XXX',
    });

    this.cardCvcElement.mount(this.cardCvc().nativeElement);
  }

  private handleElementsValueChange(): void {
    const watchStateValidity = (element: StripeElementWithHandlers) => {
      return new Observable((subscriber: Subscriber<boolean>) => {
        const handler = (event: StripeElementChangeEvent) => {
          return subscriber.next(event.complete && !event.error);
        };

        element.on('change', handler);

        return () => element.off('change', handler);
      });
    };

    combineLatest([
      watchStateValidity(this.cardNumberElement),
      watchStateValidity(this.cardExpiryElement),
      watchStateValidity(this.cardCvcElement),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .pipe(map((results: boolean[]) => results.every(Boolean)))
      .subscribe((isValid: boolean) => this.#isValid.set(isValid));
  }

  private onElementsReady(): Observable<void> {
    const observeWhenReady = (element: StripeElementWithHandlers) => {
      return new Observable((subscriber: Subscriber<void>) => {
        element.once('ready', () => {
          subscriber.next();
          subscriber.complete();
        });
      });
    };

    return combineLatest([
      observeWhenReady(this.cardNumberElement),
      observeWhenReady(this.cardExpiryElement),
      observeWhenReady(this.cardCvcElement),
    ]).pipe(map(() => undefined));
  }

  private generateElementOptions(textCentered: boolean = false): StripeCardElementOptions {
    return {
      style: {
        base: {
          fontSize: '14px',
          letterSpacing: '0.41px',
          fontFamily: 'GreycliffCF-Regular',
          ...(textCentered ? { textAlign: 'center' } : {}),
          '::placeholder': {
            color: '#676767',
          },
        },
      },
      classes: { focus: 'gus-card-input--focus', invalid: 'gus-card-input--invalid' },
    };
  }
}
